import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Grid";

const transition = "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1)";

function gradientForColor(color) {
    switch (color) {
        case "success":
            return "linear-gradient(90deg, rgba(58, 156, 96, 0.85), rgba(58, 156, 96, 1))";
        case "error":
            return "linear-gradient(90deg, rgba(251, 105, 98, 0.85), rgba(251, 105, 98, 1))";
        case "info":
            return "linear-gradient(90deg, rgba(0, 103, 184, 0.85), rgba(46, 170, 250, 1))";
        default:
            return "linear-gradient(90deg, rgba(255, 144, 21, 0.85), rgba(255, 193, 0, 1))";
    }
}

function shadowForColor(color) {
    switch (color) {
        case "success":
            return "0 2px 8px rgba(58, 156, 96, 0.30)";
        case "error":
            return "0 2px 8px rgba(251, 105, 98, 0.30)";
        case "info":
            return "0 2px 8px rgba(0, 103, 184, 0.30)";
        default:
            return "0 2px 8px rgba(255, 144, 21, 0.30)";
    }
}

function MobileKpiCard({ title, value, progress, color }) {
    return (
        <Card
            sx={{
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(255, 144, 21, 0.05), 0 1px 8px rgba(20, 24, 44, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.55)",
                background:
                    "linear-gradient(160deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.60) 100%)",
                backdropFilter: "blur(16px) saturate(1.4)",
                WebkitBackdropFilter: "blur(16px) saturate(1.4)",
                position: "relative",
                overflow: "hidden",
                transition,
                "&:active": {
                    transform: "scale(0.97)",
                },
            }}
        >
            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 }, textAlign: "center" }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: "rgba(107, 114, 128, 0.85)",
                        fontWeight: 500,
                        fontSize: "0.62rem",
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                        mb: 0.25,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        color: "rgba(20, 24, 44, 0.88)",
                        lineHeight: 1.1,
                    }}
                >
                    {value}
                </Typography>
                <LinearProgress
                    value={progress}
                    color={color}
                    variant="determinate"
                    sx={{
                        mt: 1,
                        height: 4,
                        borderRadius: 999,
                        backgroundColor: "rgba(148, 163, 184, 0.12)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            backgroundImage: gradientForColor(color),
                            boxShadow: shadowForColor(color),
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
}

export default function MobileDashboardKpis({ pendingLeaves, leaveStats }) {
    const total = leaveStats.total || 0;

    const kpis = [
        {
            title: "En attente",
            value: pendingLeaves,
            progress: total ? (leaveStats.pending / total) * 100 : 0,
        },
        {
            title: "Validées",
            value: leaveStats.validated,
            progress: total ? (leaveStats.validated / total) * 100 : 0,
            color: "success",
        },
        {
            title: "Publiées",
            value: leaveStats.published,
            progress: total ? (leaveStats.published / total) * 100 : 0,
            color: "info",
        },
        {
            title: "Suppressions",
            value: leaveStats.deleted + leaveStats.deleteRequested,
            progress: total
                ? ((leaveStats.deleted + leaveStats.deleteRequested) / total) * 100
                : 0,
            color: "error",
        },
    ];

    return (
        <Box>
            <Grid container spacing={1} justifyContent="center">
                {kpis.map((kpi) => (
                    <Grid item xs={3} key={kpi.title} sx={{ textAlign: "center" }}>
                        <MobileKpiCard
                            title={kpi.title}
                            value={kpi.value}
                            progress={kpi.progress}
                            color={kpi.color}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
